import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useRegion } from '../hooks/useRegion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Loader2, TrendingUp, TrendingDown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

export function ROIRingCard({ disableLink = false }) {
  const navigate = useNavigate();
  const { roiProjection, roiProjectionLoading, fetchROIProjection } = useAppStore();
  const { formatCurrency } = useRegion();

  useEffect(() => {
    fetchROIProjection();
  }, [fetchROIProjection]);

  if (roiProjectionLoading) {
    return (
      <Card className="relative overflow-hidden bg-surface rounded-tile border-0 h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  if (!roiProjection?.has_data) {
    return (
      <Card className="relative overflow-hidden bg-surface rounded-tile border-0 h-full flex items-center justify-center">
        <CardContent className="text-center relative">
          <p className="text-sm text-muted-foreground">No ROI data</p>
        </CardContent>
      </Card>
    );
  }

  const { portfolio } = roiProjection;
  
  // Active equipment calculations
  const activeSpent = portfolio.total_value || 0;
  const activeIncome = portfolio.active_income || 0;  // Use active_income from backend
  const activeROI = activeSpent > 0 ? ((activeIncome - activeSpent) / activeSpent) * 100 : 0;
  const activeROIPositive = activeROI >= 0;
  
  // All equipment calculations
  const allSpent = portfolio.all_equipment_value || activeSpent;
  const allIncome = portfolio.total_income || 0;  // This is all equipment income
  const allROI = allSpent > 0 ? ((allIncome - allSpent) / allSpent) * 100 : 0;
  const allROIPositive = allROI >= 0;

  return (
    <Card 
      className={`relative overflow-hidden bg-surface rounded-tile border-0 h-full flex flex-col ${disableLink ? "" : "cursor-pointer hover:bg-mist transition-colors"}`}
      onClick={disableLink ? undefined : () => navigate('/equipment')}
    >
      <CardHeader className="pb-2 relative px-6 pt-6 flex-shrink-0">
        <div className="flex items-center gap-2">
          <p className="eyebrow leading-tight">equipment ROI</p>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-2 relative pb-6 pt-0 px-6 flex-1 flex flex-col justify-end">
        <TooltipProvider delayDuration={0}>
          {/* Row 1: Active Equipment */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-between p-3 bg-mist rounded-cards cursor-help">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">Amount spent: active</p>
                  <p className="text-lg text-foreground">{formatCurrency(activeSpent)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {activeROIPositive ? (
                    <TrendingUp className="w-4 h-4 text-moss" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-brick" />
                  )}
                  <p className={`text-2xl font-light ${activeROIPositive ? 'text-moss' : 'text-brick'}`}>
                    {activeROI >= 0 ? '+' : ''}{activeROI.toFixed(0)}%
                  </p>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs text-inverse-muted">Total income (active)</p>
              <p className="text-sm text-inverse-foreground">{formatCurrency(activeIncome)}</p>
            </TooltipContent>
          </Tooltip>
          
          {/* Row 2: All Equipment */}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-between p-3 bg-mist rounded-cards cursor-help">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">Amount spent: all</p>
                  <p className="text-lg text-foreground">{formatCurrency(allSpent)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {allROIPositive ? (
                    <TrendingUp className="w-4 h-4 text-moss" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-brick" />
                  )}
                  <p className={`text-2xl font-light ${allROIPositive ? 'text-moss' : 'text-brick'}`}>
                    {allROI >= 0 ? '+' : ''}{allROI.toFixed(0)}%
                  </p>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs text-inverse-muted">Total income (all equipment)</p>
              <p className="text-sm text-inverse-foreground">{formatCurrency(allIncome)}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </CardContent>
    </Card>
  );
}
